import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import pluralize from 'pluralize';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { hasRoleAdmin } from 'common/userRoles';
import { getStatusColor, JOB_ORDER_STATUS_CANCELLED } from 'common/jobConstants';

import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrAdminNotes, {
  ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES,
} from 'client/components/SrAdminNotes';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrSunSetupListingOrderDetail from 'client/components/SrSunSetupListingOrder/SrSunSetupListingOrderDetail';
import SrSunSetupListingOrderJob from 'client/components/SrSunSetupListingOrder/SrSunSetupListingOrderJob';
import SrPartnerList from 'client/components/SrPartnerList';
import SrLink from 'client/components/SrLink/SrLink';
import SrTag, {
  SR_TAG_THEME_LIGHTBLUE_BLACK,
  SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER,
  SR_TAG_THEME_WHITE_BLACK,
} from 'client/components/SrTag/SrTag';

const SrSunSetupListingOrder = ({ listing, jobOrder, currentUser, reloadItems }) => {
  const _getJobsWithDriverNotes = (jobOrder) => {
    return (jobOrder.jobs || []).filter(({ driverNotes }) => !!driverNotes);
  };

  const _getNoteTagTheme = () => {
    const isAdmin = hasRoleAdmin(currentUser.roles);
    if (jobOrder.noteCount === 0) {
      return SR_TAG_THEME_WHITE_BLACK;
    }
    if (isAdmin) {
      return SR_TAG_THEME_LIGHTBLUE_BLACK_HOVER;
    }
    return SR_TAG_THEME_LIGHTBLUE_BLACK;
  };

  const jobLatestStatusUpdate = jobOrder.latestStatusUpdate || null;
  const jobsWithDriverNotes = _getJobsWithDriverNotes(jobOrder);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const dispatch = useDispatch();

  const editBackUrl = global.encodeURIComponent(`/sunsetup/listing-orders/${listing.id}`);
  const noteTagTheme = _getNoteTagTheme();

  return (
    <SrCard margin="0 0">
      <SrFlex align="center" justify="between">
        <SrBox>
          <SrText font="greycliff" fontSize="21px" weight="800">
            Order # {jobOrder.id}
          </SrText>
        </SrBox>
        <SrFlex align="center">
          <SrBox marginRight="16px" cursor="pointer">
            <SrTag
              theme={noteTagTheme}
              variation="goingToMarketCard"
              onClick={() => setIsNotesModalOpen(true)}
            >
              {pluralize('Note', jobOrder.noteCount, true)}
            </SrTag>
          </SrBox>
          {jobOrder.driverId && jobOrder.status !== JOB_ORDER_STATUS_CANCELLED && (
            <SrBox marginRight="16px">
              <SrLink
                color="blue300"
                to={`/sunsetup/drivers/${jobOrder.driverId}/orders/${jobOrder.id}`}
              >
                View as Driver
              </SrLink>
            </SrBox>
          )}
          <SrLink
            to={`/sunsetup/orders/edit/${jobOrder.id}?backUrl=${editBackUrl}`}
            color="blue300"
          >
            Edit
          </SrLink>
        </SrFlex>
      </SrFlex>
      <SrFlex marginTop="16px" justify="between">
        <SrSunSetupListingOrderDetail
          title="Due Date"
          subtitle={moment(jobOrder.dueDate).format('MMM DD') || 'Missing due date'}
          maxWidth="24%"
        />
        <SrSunSetupListingOrderDetail
          title="Status"
          subtitle={jobOrder.status}
          subtitleColor={getStatusColor(jobOrder.status)}
          maxWidth="24%"
        />
        <SrSunSetupListingOrderDetail
          title="Driver"
          subtitle={jobOrder.driverName || 'Unknown'}
          maxWidth="24%"
        />
        <SrSunSetupListingOrderDetail
          title="Submitted by"
          subtitle={jobOrder.requestedBy || 'Sunroom API'}
          maxWidth="24%"
        />
      </SrFlex>
      {jobLatestStatusUpdate && (
        <SrFlex marginTop="16px" justify="between">
          <SrSunSetupListingOrderDetail
            title="Status Updated by"
            subtitle={jobLatestStatusUpdate.updatedByUserName || 'Unknown'}
            maxWidth="45%"
          />
          <SrSunSetupListingOrderDetail
            title="Status Updated at"
            subtitle={
              momentWithAustinTimezone(jobLatestStatusUpdate.createdDate).format(
                'MMM Do [at] h:mm A [CST]',
              ) || 'Unknown'
            }
            maxWidth="45%"
          />
        </SrFlex>
      )}
      <SrBox marginTop="24px">
        <SrBox marginBottom="-15px">
          <SrText font="greycliff" fontSize="18px" weight="800">
            Jobs
          </SrText>
        </SrBox>
        <SrBox padding="0px 8px">
          <SrPartnerList twoColumns>
            {(jobOrder.jobs || []).map((job) => (
              <SrSunSetupListingOrderJob key={job.id} job={job} listing={listing} showPhotos />
            ))}
          </SrPartnerList>
        </SrBox>
      </SrBox>
      <SrBox marginTop="16px" maxWidth="300px">
        <SrBox>
          <SrText weight="800" fontSize="16px">
            Order Instructions
          </SrText>
        </SrBox>
        <SrBox width="100%">
          <SrText font="greycliff" weight="600" fontSize="14px" whiteSpace="prewrap">
            {jobOrder.instructions || 'No instructions provided.'}
          </SrText>
        </SrBox>
      </SrBox>

      {jobsWithDriverNotes.length > 0 && (
        <SrBox marginTop="16px" maxWidth="300px">
          <SrBox>
            <SrText weight="800" fontSize="16px">
              Driver Notes
            </SrText>
          </SrBox>
          <SrBox width="100%">
            {jobsWithDriverNotes.map((job, index) => (
              <SrBox paddingTop={index === 0 ? null : '16px'} key={job.id}>
                <SrSunSetupListingOrderJob job={job} listing={listing} />
                <SrText font="greycliff" weight="600" fontSize="14px" whiteSpace="prewrap">
                  {job.driverNotes}
                </SrText>
              </SrBox>
            ))}
          </SrBox>
        </SrBox>
      )}
      {isNotesModalOpen && (
        <SrModalV2
          closeOnBackgroundClick
          showCloseButton={false}
          contentMaxWidth="546px"
          contentPadding="0px"
          theme={MODAL_THEME_STACK}
          onClose={() => setIsNotesModalOpen(false)}
        >
          <SrCard
            header="Job Order Notes (Admin-Only)"
            subheader={`Order # ${jobOrder.id}`}
            color={SR_CARD_BULLET_COLOR_BLUE}
            maxWidth="none"
          >
            <SrAdminNotes
              jobOrderId={jobOrder.id}
              variation={ADMIN_NOTES_VARIATION_JOB_ORDER_NOTES}
              dispatch={dispatch}
              onNoteCreated={reloadItems}
              onNoteDeleted={reloadItems}
              currentUser={currentUser}
            />
          </SrCard>
        </SrModalV2>
      )}
    </SrCard>
  );
};

SrSunSetupListingOrder.propTypes = {
  listing: PropTypes.object.isRequired,
  jobOrder: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  reloadItems: PropTypes.func.isRequired,
};

SrSunSetupListingOrder.displayName = 'SrSunSetupListingOrder';

export default SrSunSetupListingOrder;
